import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  FilePlus2,
  Link2,
  ClipboardList,
  CheckCircle2,
  XCircle,
  ShieldCheck,
  ShieldX,
  Activity,
} from "lucide-react";

const KIND_META = {
  risk_created: { icon: FilePlus2, color: "text-ek-purple", label: "Risk created" },
  signal_linked: { icon: Link2, color: "text-blue-600", label: "Signal linked" },
  playbook_assigned: { icon: ClipboardList, color: "text-amber-600", label: "Playbook assigned" },
  playbook_completed: { icon: CheckCircle2, color: "text-emerald-600", label: "Playbook completed" },
  playbook_aborted: { icon: XCircle, color: "text-red-500", label: "Playbook aborted" },
  remediation_requested: { icon: Activity, color: "text-amber-600", label: "Remediation requested" },
  remediation_executed: { icon: ShieldCheck, color: "text-emerald-600", label: "Remediation executed" },
  remediation_rejected: { icon: ShieldX, color: "text-red-500", label: "Remediation rejected" },
};

const fmt = (ts) => {
  try {
    return new Date(ts).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return ts;
  }
};

export const RiskActivityTimeline = ({ riskId }) => {
  const [events, setEvents] = useState(null);

  useEffect(() => {
    let active = true;
    api
      .get(`/risks/${riskId}/activity`)
      .then(({ data }) => active && setEvents(data.events))
      .catch(() => active && setEvents([]));
    return () => {
      active = false;
    };
  }, [riskId]);

  return (
    <div className="mt-8" data-testid="risk-activity-timeline">
      <h3 className="ek-eyebrow mb-3 flex items-center gap-1">
        <Activity size={12} /> Activity timeline {events ? `(${events.length})` : ""}
      </h3>
      {events === null ? (
        <p className="text-sm text-ek-ink/30">Loading activity…</p>
      ) : events.length === 0 ? (
        <p className="text-sm text-ek-ink/30">No activity recorded yet.</p>
      ) : (
        <ol className="relative space-y-4 border-l border-black/10 pl-5">
          {events.map((e, i) => {
            const meta = KIND_META[e.kind] || { icon: Activity, color: "text-ek-ink/65", label: e.kind };
            const Icon = meta.icon;
            return (
              <li key={i} data-testid="activity-event" className="relative">
                <span
                  className={`absolute -left-[27px] flex h-5 w-5 items-center justify-center rounded-full border border-black/5 bg-white ${meta.color}`}
                >
                  <Icon size={11} />
                </span>
                <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                  <span className="text-sm font-semibold text-ek-ink">{meta.label}</span>
                  <span className="text-[11px] text-ek-ink/65">{fmt(e.ts)}</span>
                </div>
                {e.detail && <p className="mt-0.5 text-xs text-ek-ink/60">{e.detail}</p>}
                <p className="mt-0.5 text-[11px] text-ek-ink/65">
                  by <span className="font-medium text-ek-ink/65">{e.actor_name || "system"}</span>
                </p>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};
